import {Dialog} from './Dialog'
import { Injectable } from '../../injector'

import * as styles from './Dialog.css'

export interface DialogContent {
    container?: JQuery;
    tournamentId?: number;
    okButtonText?: string;
    message?: string;
    displayCancelButton?: boolean;
    header?: string;
    tableId?: number;
    translationKey?: string;
    cancelCallback?: () => void;
    cancelButtonText?: string;
}

export enum DIALOG {
    BODY = 'body',
    CONTENT = 'new-ui-dialog-content',
    HEADER = 'new-ui-dialog-header',
    MESSAGE = 'new-ui-dialog-message',
    BUTTONS = 'new-ui-dialog-buttons',
    OK_BUTTON = 'new-ui-dialog-ok-button',
    CANCEL_BUTTON = 'new-ui-dialog-cancel-button'
}

@Injectable()
export class DialogManager {
	currentCloseCallback: () => void
	open: boolean
	currentDialog: Dialog
	dialogQueue: { dialog: Dialog, okCallback: any, closeCallback: () => void }[]
	container: JQuery
	private dialogRegistry: Map<string, Dialog> = new Map()

	constructor() {
		this.currentCloseCallback = null
		this.open = false
		this.currentDialog = null
		this.dialogQueue = []
		const c = $('<div/>').attr('id', 'genericDialogContainer')
		$(DIALOG.BODY).append(c)
		this.container = $('#genericDialogContainer')
	}

	openQueuedDialog(): void {
		if (this.dialogQueue.length > 0) {
			const d = this.dialogQueue[0]
			this.dialogQueue.splice(0, 1)
			this.displayDialog(d.dialog, d.okCallback, d.closeCallback)
		}
	}

	queueDialog(dialog: Dialog, okCallback: () => void | boolean, closeCallback: () => void): void {
		this.dialogQueue.push({
			dialog: dialog,
			okCallback: okCallback,
			closeCallback: closeCallback
		})
	}

	/**
     * Displays a generic dialog with a header, message and a continue button
     * example
     * displayManager.displayGenericDialog({header : "header" , message:"message", okButtonText : "reload"});
     * @param {Object} content - the content of the dialog, see above for format
     * @param {String} content.header - header of the dialog
     * @param {String} content.message - the message to display
     * @param {Number} content.tableId - the id of the table to use as context
     * @param {Number} content.tournamentId - the id of the tournament to use as context
     * @param {String} [content.translationKey] - optional translation key to use
     * @param {Boolean} [content.displayCancelButton] - if you should display a cancel
     * @param {Function} [okCallback] callback to execute when ok button is clicked
     */
	displayGenericDialog(content: DialogContent, okCallback?: () => void | boolean, hideOnOk?:boolean ): string {
		if (content.translationKey === undefined) {
			const i18nKey = 'dialogs.' + content.message + '.header'
			const i18ntHeader = i18n.t('dialogs.' + content.message + '.header')
			if (i18ntHeader && (i18nKey !== i18ntHeader)) {
				content.translationKey = content.message
			}
		}

		if (content.translationKey !== undefined) {
			content = $.extend(content, {
				header: i18n.t('dialogs.' + content.translationKey + '.header'),
				message: i18n.t('dialogs.' + content.translationKey + '.message')
			})
		}

		const container = DialogManager.getContainer(content)

		let tableId = null
		if (content.tableId !== undefined) {
			tableId = content.tableId
		}

		const genericDialog = new Dialog(container, $('#genericDialog'), tableId)
		const element = genericDialog.getElement()
		element.find('.' + DIALOG.BUTTONS).addClass(styles.dialogButtons)

		if (content.header) {
			element.find('.' + DIALOG.HEADER).html(content.header).addClass(styles.dialogHeader + ' skin__cell-header')
		} else {
			element.find('.' + DIALOG.HEADER).hide()
		}

		if (content.message) {
			element.find('.' + DIALOG.MESSAGE).html(content.message).addClass(styles.dialogMessage)
		} else {
			element.find('.' + DIALOG.MESSAGE).hide()
		}

		if (content.displayCancelButton) {
			element.find('.' + DIALOG.CANCEL_BUTTON).show().addClass(styles.dialogButton + ' skin__primary-button active-active')
				.attr('data-testid', 'dialog-cancel-button')
			if (content.cancelButtonText) {
				element.find('.' + DIALOG.CANCEL_BUTTON).html(content.cancelButtonText)
			}
		} else {
			element.find('.' + DIALOG.CANCEL_BUTTON).hide()
		}

		element.find('.' + DIALOG.OK_BUTTON).addClass(styles.dialogButton + ' skin__secondary-button')
			.attr('data-testid', 'dialog-ok-button')
		if (content.okButtonText !== undefined) {
			element.find('.' + DIALOG.OK_BUTTON).html(content.okButtonText)
		}

		if (okCallback === undefined) {
			this.displayDialog(genericDialog, () => {
				genericDialog.close()
			}, content.cancelCallback)
		} else {
			this.displayDialog(genericDialog, () => {
				hideOnOk && genericDialog.close()
				return okCallback()
			}, content.cancelCallback)
		}
		this.dialogRegistry.set(genericDialog.id, genericDialog)
		return genericDialog.id
	}

	static getContainer(content: DialogContent): JQuery {

		let container = null

		if (content.tableId !== undefined) {
			container = DialogManager.getTableContainer(content.tableId)
		} else if (content.tournamentId !== undefined) {
			container = DialogManager.getTournamentLobbyContainer(content.tournamentId)
		} else if (content.container !== undefined) {
			container = content.container
		} else {
			container = $(DIALOG.BODY)
		}

		return container
	}

	static getTableContainer(tableId: number): JQuery {
		return null
	}

	static getTournamentLobbyContainer(tournamentId: number): JQuery {
		const tournament = $('#tournamentView' + tournamentId)
		return tournament.length > 0 ? tournament : null
	}

	/**
     * Display a dialog by passing a DOM element id you want to be placed in
     * the dialog, if a dialog is open it will be queued and showed when
     * previous dialog is closed
     * @param {Dialog} dialog
     * @param okCallback
     * @param closeCallback
     */
	displayDialog(dialog: Dialog, okCallback: () => void | boolean, closeCallback: () => void): void {
		if (closeCallback) {
			this.currentCloseCallback = closeCallback
		}

		let targetFontSize
		if (dialog.parentContainer == $(DIALOG.BODY)) {
			targetFontSize = Math.round(90 * $(window).width() / 1024)
			if (targetFontSize > 125) {
				targetFontSize = 125
			}
		}

		const dialogElement = dialog.getElement()
		dialogElement.css({fontSize: targetFontSize + '%'})
		dialogElement.find('.' + DIALOG.CANCEL_BUTTON).touchSafeClick(() => {
			if (this.currentCloseCallback) {
				this.currentCloseCallback()
			}
			dialog.close()
		})
		dialogElement.find('.' + DIALOG.OK_BUTTON).touchSafeClick(() => {
			if (okCallback() || !okCallback) {
				dialog.close()
			}
		})
		dialog.show()

		this.currentDialog = dialog
	}

	closeDialogById(dialogId: string) {
		this.dialogRegistry.get(dialogId)?.close()
	}
}
